import React, { useEffect, useState } from 'react';
import { Save, ToggleLeft, ToggleRight, Info, AlertTriangle, CheckCircle, MessageSquare, Calendar, Clock, Plus, Trash2, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { useApi } from './DashboardLayout';
import { DefaultEditor, BtnBold, BtnItalic, Toolbar } from 'react-simple-wysiwyg';

const BANNER_TYPES = [
  { value: 'info', label: 'Information', color: '#3B82F6', icon: Info, desc: 'Blauer Hinweis' },
  { value: 'warning', label: 'Warnung', color: '#F59E0B', icon: AlertTriangle, desc: 'Gelbe Warnung' },
  { value: 'success', label: 'Erfolg', color: '#10B981', icon: CheckCircle, desc: 'Grüne Mitteilung' },
];

function ScheduleBadge({ validFrom, validUntil, enabled }) {
  if (!enabled) return <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600 }}>Deaktiviert</span>;
  if (!validFrom && !validUntil) return (
    <span style={{ fontSize: 11, fontWeight: 600, color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981' }} /> Dauerhaft
    </span>
  );
  const today = new Date().toISOString().split('T')[0];
  if (validFrom && today < validFrom) {
    return <span style={{ fontSize: 11, fontWeight: 600, color: '#6366F1', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={11} /> Geplant</span>;
  }
  if (validUntil && today > validUntil) {
    return <span style={{ fontSize: 11, fontWeight: 600, color: '#EF4444', display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#EF4444' }} /> Abgelaufen</span>;
  }
  return <span style={{ fontSize: 11, fontWeight: 600, color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981' }} /> Aktiv</span>;
}

function BannerEditor({ banner, onChange, onDelete, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const typeInfo = BANNER_TYPES.find(t => t.value === banner.banner_type) || BANNER_TYPES[0];
  const anyEnabled = banner.enabled_admin || banner.enabled_provider;
  const plainText = banner.content ? banner.content.replace(/<[^>]*>/g, '') : '';
  const preview = plainText ? (plainText.length > 60 ? plainText.substring(0, 60) + '...' : plainText) : 'Kein Text';

  const set = (key, val) => onChange({ ...banner, [key]: val });

  const toggleBanner = (e) => {
    e.stopPropagation();
    const newState = !anyEnabled;
    onChange({ ...banner, enabled_admin: newState, enabled_provider: newState });
  };

  return (
    <div className="card" style={{ borderLeft: `3px solid ${anyEnabled ? typeInfo.color : '#D1D5DB'}`, marginBottom: '1rem', opacity: anyEnabled ? 1 : 0.6, transition: 'all 0.15s' }} data-testid={`banner-editor-${banner.id}`}>
      {/* Header / Collapsed View */}
      <div
        onClick={() => setOpen(!open)}
        style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, userSelect: 'none' }}
        data-testid={`banner-toggle-${banner.id}`}
      >
        <div style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
        <div style={{
          width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: anyEnabled ? `${typeInfo.color}1A` : 'var(--bg, #f0f0f0)', flexShrink: 0,
        }}>
          <typeInfo.icon size={14} style={{ color: anyEnabled ? typeInfo.color : '#9CA3AF' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: anyEnabled ? 'var(--text)' : 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {typeInfo.label}
            {banner.enabled_admin && ' · Admin'}
            {banner.enabled_provider && ' · Mitglieder'}
          </div>
        </div>
        <ScheduleBadge validFrom={banner.valid_from} validUntil={banner.valid_until} enabled={anyEnabled} />
        <button onClick={toggleBanner} data-testid={`banner-switch-${banner.id}`}
          title={anyEnabled ? 'Banner deaktivieren' : 'Banner aktivieren'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: anyEnabled ? 'var(--primary)' : '#9CA3AF', padding: 0, display: 'flex', flexShrink: 0 }}>
          {anyEnabled ? <ToggleRight size={30} /> : <ToggleLeft size={30} />}
        </button>
      </div>

      {/* Expanded Editor */}
      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border-color, #eee)' }}>
          {/* Toggles Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 16, marginBottom: 16 }}>
            <ToggleRow label="Admin" active={banner.enabled_admin} onToggle={() => set('enabled_admin', !banner.enabled_admin)} testId={`banner-admin-${banner.id}`} />
            <ToggleRow label="Mitglieder" active={banner.enabled_provider} onToggle={() => set('enabled_provider', !banner.enabled_provider)} testId={`banner-provider-${banner.id}`} />
            <ToggleRow label="Ticket-Button" active={banner.show_ticket_link} onToggle={() => set('show_ticket_link', !banner.show_ticket_link)} testId={`banner-ticket-${banner.id}`} />
          </div>

          {/* Type Selector */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {BANNER_TYPES.map(t => {
              const Icon = t.icon;
              const sel = banner.banner_type === t.value;
              return (
                <button key={t.value} onClick={() => set('banner_type', t.value)}
                  data-testid={`banner-type-${t.value}-${banner.id}`}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '8px 10px', borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s', fontSize: 12, fontWeight: sel ? 700 : 500,
                    border: sel ? `2px solid ${t.color}` : '2px solid var(--border-color, #e0e0e0)',
                    background: sel ? `${t.color}0D` : 'transparent',
                    color: sel ? t.color : 'var(--text-muted)',
                  }}>
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>

          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Sichtbar ab</label>
              <input type="date" className="form-input" value={banner.valid_from || ''} onChange={e => set('valid_from', e.target.value || null)}
                data-testid={`banner-from-${banner.id}`} style={{ fontSize: 12 }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Sichtbar bis</label>
              <input type="date" className="form-input" value={banner.valid_until || ''} onChange={e => set('valid_until', e.target.value || null)}
                data-testid={`banner-until-${banner.id}`} style={{ fontSize: 12 }} />
            </div>
          </div>

          {/* WYSIWYG Text Editor */}
          <div style={{ marginBottom: 12 }} data-testid={`banner-editor-wysiwyg-${banner.id}`}>
            <DefaultEditor
              value={banner.content}
              onChange={e => set('content', e.target.value)}
              style={{ fontSize: 13, lineHeight: 1.6, minHeight: 80 }}
            >
              <Toolbar>
                <BtnBold />
                <BtnItalic />
              </Toolbar>
            </DefaultEditor>
          </div>

          {/* Delete */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={onDelete} data-testid={`banner-delete-${banner.id}`}
              style={{ fontSize: 12, color: '#EF4444', background: 'none', border: '1px solid #FCA5A5', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Trash2 size={12} /> Löschen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ToggleRow({ label, active, onToggle, testId }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--bg)', borderRadius: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
      <button onClick={onToggle} data-testid={testId}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: active ? 'var(--primary)' : '#9CA3AF', padding: 0, display: 'flex' }}>
        {active ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
      </button>
    </div>
  );
}

export default function GeneralSettingsPage() {
  const { apiFetch } = useApi();
  const [banners, setBanners] = useState([]);
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    apiFetch('/api/settings/info-banner/admin').then(r => r?.json()).then(d => {
      if (d?.banners) setBanners(d.banners);
      if (d?.global_enabled !== undefined) setGlobalEnabled(d.global_enabled);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const addBanner = () => {
    setBanners([...banners, {
      id: String(Date.now()),
      content: '',
      banner_type: 'info',
      enabled_admin: true,
      enabled_provider: true,
      show_ticket_link: true,
      valid_from: null,
      valid_until: null,
      sort_order: banners.length,
      created_at: new Date().toISOString(),
    }]);
  };

  const updateBanner = (idx, updated) => {
    const copy = [...banners];
    copy[idx] = updated;
    setBanners(copy);
  };

  const deleteBanner = (idx) => {
    setBanners(banners.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true); setMsg('');
    const r = await apiFetch('/api/settings/info-banner', {
      method: 'PUT',
      body: JSON.stringify({ banners, global_enabled: globalEnabled }),
    });
    if (r?.ok) setMsg('Gespeichert');
    else setMsg('Fehler beim Speichern');
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const activeCount = banners.filter(b => {
    if (!b.enabled_admin && !b.enabled_provider) return false;
    const today = new Date().toISOString().split('T')[0];
    if (b.valid_from && today < b.valid_from) return false;
    if (b.valid_until && today > b.valid_until) return false;
    return true;
  }).length;

  return (
    <div data-testid="general-settings-page">
      <div className="page-header">
        <h2>Allgemein</h2>
        <p>Allgemeine Portal-Einstellungen</p>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="card-header-icon" style={{ background: 'rgba(59,130,246,0.1)', color: 'var(--primary)' }}><Info size={18} /></span>
            Info-Banner
            {banners.length > 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 }}>({activeCount} aktiv von {banners.length})</span>}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={addBanner} data-testid="add-banner-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
              <Plus size={14} /> Neues Banner
            </button>
          </div>
        </div>
        <div className="card-body">
          {!loaded ? <p>Laden...</p> : (
            <>
              {banners.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                  <Info size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
                  <p style={{ fontSize: 14, margin: '0 0 4px' }}>Noch keine Banner erstellt</p>
                  <p style={{ fontSize: 12 }}>Erstellen Sie Info-Banner, um wichtige Nachrichten auf dem Dashboard anzuzeigen.</p>
                </div>
              ) : (
                <>
                  {banners.map((b, idx) => (
                    <BannerEditor
                      key={b.id}
                      banner={b}
                      onChange={(updated) => updateBanner(idx, updated)}
                      onDelete={() => deleteBanner(idx)}
                      defaultOpen={banners.length === 1 || !b.content}
                    />
                  ))}
                </>
              )}

              {banners.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                  <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="info-banner-save">
                    <Save size={14} /> {saving ? 'Speichern...' : 'Alle Banner speichern'}
                  </button>
                  {msg && <span style={{ fontSize: 13, fontWeight: 600, color: msg === 'Gespeichert' ? '#10B981' : '#EF4444' }}>{msg}</span>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
